/**
 * UserDo adapter for the unified sync engine.
 *
 * Owns the user-scope wiring: which store to read/write, what to do on
 * `sync-state` / `sync-diff`, and how to detect locally-dirty changes
 * that need outbound pushes.
 *
 * The engine handles the universal plumbing (decode, log, op-ack /
 * op-rejected, persistence-error). The adapter only knows about
 * UserDo's protocol shape.
 */

import { setCardHistoryJoinDisabled } from '@flashka/shared/sync-engine/framework/card-history-join';
import { getSyncClientCapabilities } from '@flashka/shared/sync-engine/framework/client-capabilities';
import { recordEntities } from '@flashka/shared/sync-engine/framework/entity-registry';
import {
  applySyncDiff as applySyncDiffPure,
  buildSyncInit as buildSyncInitPure,
  detectDirtyChangesDelta,
} from '@flashka/shared/sync-engine/framework/sync-engine';
import { mergeCardsLWW } from '@flashka/shared/sync-engine/framework/sync-merge';
import { partitionDecksByWarmth } from '@flashka/shared/sync-engine/schema/cold-deck';
import type { SyncNoteEntry } from '@flashka/shared/sync-engine/schema/shared-types';
import type {
  ActivitySummary,
  SyncClientMessage,
  SyncServerMessage,
  UserDoServerMessage,
} from '@flashka/shared/sync-engine/schema/sync-types';

// Resolves on native too — @sentry/react-native depends on @sentry/react
// (see flashka-app/metro.config.js notes and telemetry/scope-observer.ts).
import * as Sentry from '@sentry/react';

import type { AccountSeedEvent } from '../../account-seed';
import { getSyncStorage, type SyncStorage } from '../../provider/sync-storage';
import { notifyDeckRehydrated } from '../cold-deck-rehydrate';
import { bucketRowCount, deckBucketComplete } from '../deck-residency';
import { drainPendingBulk } from '../entities/bulk-coalesce';
import {
  clientBroadcastMap,
  clientEntities,
  clientEntityByStateKey,
} from '../entities/client-entities';
import { getMutationSource, withServerSource } from '../framework/mutation-source';
import type { SyncCtx, SyncStatus } from '../hook/sync-context-types';
import { perfLog, perfMark, perfNow } from '../perf-log'; // [perf-probe]
import { setPersonalSyncReady } from '../personal-sync-ready';
import { buildSnapshot } from '../snapshot-builder';
import { cleanStoredCard } from '../store/card-rows';
import { type FlashkaState, useFlashkaStore } from '../store/flashka-store';
import { unackedProgressFloorDecks } from '../store/progress-floor';
import type { RejectReason } from '../sync-bookkeeping/pending-mutations';
import { getUnsyncedDeckTracker } from '../sync-bookkeeping/unsynced-decks';
import { uuid } from '../uuid';
import type { ScopeAdapter, SendRaw } from './use-do-sync';

// ---------------------------------------------------------------------------
// Adapter deps
// ---------------------------------------------------------------------------

/**
 * Platform-supplied bridge for the DO-driven legacy migration's
 * `legacy-notes-bundle` payload. Web wires a Dexie-backed stash; native
 * leaves this undefined (no notes to persist locally — the user-side
 * Tiptap features only ship on web).
 *
 * The adapter ALWAYS sends `legacy-notes-stashed-ack` regardless of
 * whether stashing succeeded, so the DO can release the bundle. If
 * `stashLegacyNotes` is undefined or throws, the failure is swallowed
 * and the ack still fires — losing the legacy notes is preferable to
 * leaving the DO holding them indefinitely; the 30-day GC is a backstop
 * but not a primary failure mode.
 */
export type LegacyMigrationClient = {
  stashLegacyNotes: (
    userId: string,
    deckNotes: Record<string, Record<string, SyncNoteEntry>>,
  ) => Promise<void> | void;
};

export type UserDoAdapterDeps = {
  userId: string;
  ctx: SyncCtx;
  setActivityData: (data: ActivitySummary | null) => void;
  syncStatusRef: { current: SyncStatus };
  /** Optional platform legacy-notes stash client. Undefined on native. */
  legacyClient?: LegacyMigrationClient;
  /**
   * The seed lifecycle (`account-seed.ts`), fed from the same `sync-state`
   * messages. Kept OUT of `syncStatus`: while the DO brings a legacy account
   * over, the socket is open and nothing about the connection is wrong.
   */
  onSeedEvent?: (event: AccountSeedEvent) => void;
};

/**
 * How long the init handshake may sit unanswered (no `sync-in-sync`, no
 * `sync-diff`) before the client stops claiming 'syncing' and fails over
 * to 'error' (= render local data, retry on next reconnect). Generous —
 * a healthy server answers in single-digit seconds even for large
 * accounts; only a dead/OOM'd DO leaves the handshake hanging.
 * Exported for tests.
 */
export const SYNC_INIT_TIMEOUT_MS = 45_000;

/** First wait before re-asking after a `migration-error`. */
export const MIGRATION_RETRY_BASE_MS = 5_000;
/** Ceiling of the migration retry backoff. */
export const MIGRATION_RETRY_MAX_MS = 60_000;
/**
 * Ceiling when the DO says retrying cannot help YET (`retryable: false` — a
 * broken export object, a row too large to store): the fix is ours to ship,
 * so the client keeps asking, just less often. There is no give-up: every
 * legacy account must seed, and the user waits rather than being sent away.
 */
export const MIGRATION_RETRY_SLOW_MAX_MS = 5 * 60_000;

/**
 * How long a seed may go silent (no `sync-state` at all after `needs-seed` /
 * `migrating`) before the client asks again. The DO answers a second
 * `request-migration` while its migration lock is held with SILENCE (the
 * running seed's broadcasts reach every tab), so a run that died with its
 * isolate — lock still in storage — used to leave the user waiting until a
 * reconnect. Re-asking is harmless while a run is live (silence again); a
 * lock no live run owns is taken over at once, and the seed starts again
 * from zero (`legacy-migration.ts`). Exported for tests.
 */
export const SEED_STALL_REASK_MS = 90_000;

/**
 * Delay before the `attempt`-th (0-based) re-sent `request-migration`:
 * exponential from {@link MIGRATION_RETRY_BASE_MS}, capped at
 * {@link MIGRATION_RETRY_MAX_MS}, with "equal jitter" — a uniform pick in
 * the upper half of the step — so every tab and device waiting on the same
 * DO (they all get the broadcast error at the same instant) does not
 * re-ask in lockstep. Exported for tests.
 */
export function migrationRetryDelayMs(
  attempt: number,
  random: () => number = Math.random,
  maxMs: number = MIGRATION_RETRY_MAX_MS,
): number {
  const step = Math.min(maxMs, MIGRATION_RETRY_BASE_MS * 2 ** Math.max(0, attempt));
  return Math.round(step / 2 + random() * (step / 2));
}

// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------

export function createUserDoAdapter(deps: UserDoAdapterDeps): ScopeAdapter {
  // Plain LWW for cards until a server says it unions history (its
  // `sync-features` frame, ahead of every handshake reply). The last answer
  // then stands across reconnects: a server without the merge never sends
  // the frame.
  setCardHistoryJoinDisabled(true);
  const { ctx, setActivityData, syncStatusRef, legacyClient, userId, onSeedEvent } = deps;

  // One Sentry event per adapter instance (= per connection identity).
  // The status flip + console.error still fire every time.
  let authRejectionReported = false;

  // ── Deferred local writes ──────────────────────────────────────────
  // The dirty subscription only pushes while the status is `ready` (see
  // `subscribeDirty`). That gate is correct — during the handshake the
  // diff's own `flushLocal` owns reconciliation, and a dirty push built
  // from a pre-diff snapshot would echo the incoming diff back — but it
  // used to DISCARD the change rather than defer it, and nothing looked
  // at the store again once the handshake finished. A write made in that
  // window then reached the server only if some LATER mutation happened
  // to touch the same item again (the e2e flow's own teardown delete did
  // exactly that, which is why `folder-create-web` was green for as long
  // as nobody asked the DO).
  //
  // Measured on web 2026-09-17: a folder created ~0.8s into a large
  // account's diff apply emitted no `folders-push` at all and never
  // reached the UserDo — make a folder, close the tab, the folder is
  // gone. Same shared adapter on flashka-app, same window.
  //
  // So the gate now records that it held something back, and the
  // transition to `ready` reconciles it through `flushOfflineEdits` —
  // the same per-item "newer than what the server has" comparison a
  // reconnect would make. Replaying the captured snapshot instead would
  // re-push every row the diff just delivered.
  let hasDeferredLocalWrites = false;

  // ── Card pushes in flight ──────────────────────────────────────────
  // A card push's synced timestamps are applied on the server's ANSWER
  // (`op-ack` / `op-rejected`), not on send. The engine's `send` only knows
  // the socket says OPEN; a half-open socket (a locked phone, a network
  // switch) swallows the frame, and stamping the cursor on send made every
  // later flush judge the grade synced — it never reached another device.
  // Dropped with the socket, so the next handshake's flush re-sends the
  // rows; a re-push is idempotent on the server (the cards merge).
  const unansweredCardPushes = new Map<string, Record<string, string>>();
  // The decks of each unanswered push, marked unsynced while it waits: an
  // app killed or reloaded before the answer must re-send the rows, and only
  // a marked deck is loaded before the next handshake's flush.
  const unansweredDeckMarks = new Map<string, ReadonlyMap<string, number>>();
  const settleCardPush = (mutationId: string): boolean => {
    const stamps = unansweredCardPushes.get(mutationId);
    if (!stamps) return false;
    unansweredCardPushes.delete(mutationId);
    for (const key of Object.keys(stamps)) failedCardPushAttempts.delete(key);
    const marks = unansweredDeckMarks.get(mutationId);
    unansweredDeckMarks.delete(mutationId);
    // Clears only a deck no edit has marked since.
    if (marks) getUnsyncedDeckTracker().clearCaptured(marks);
    const live = ctx.serverTsRef.current;
    // Never wind a key back: it may have moved further while in flight.
    for (const [key, ts] of Object.entries(stamps)) {
      if (!live[key] || ts > live[key]) live[key] = ts;
    }
    // One save once the last answer is in, not one per answer: the map is
    // the whole cursor (megabytes on a large library). A kill before then
    // only re-sends rows the server already holds (a no-op there).
    if (unansweredCardPushes.size === 0) void ctx.saveServerSyncedTs(live);
    return true;
  };
  // A push the server FAILED on (its handler threw: a storage read that
  // errored, an object reset mid-request) is not a refusal of the rows.
  // Settling it like one stamped the grade synced on this device while the
  // server never stored it, so it reached no other device. It is released
  // instead, as on a dropped socket, and re-sent shortly. A frame that fails
  // every time is settled after its last try, so it cannot ride along with
  // every later flush for good. Counted per row, for this session only.
  const failedCardPushAttempts = new Map<string, number>();
  let cardPushRetryTimer: ReturnType<typeof setTimeout> | null = null;
  const clearCardPushRetry = (): void => {
    if (cardPushRetryTimer) clearTimeout(cardPushRetryTimer);
    cardPushRetryTimer = null;
  };
  const rejectCardPush = (
    mutationId: string,
    _send: SendRaw,
    reason: RejectReason,
  ): boolean => {
    // A validation refusal is final: the same rows would fail again.
    if (reason === 'validation') return settleCardPush(mutationId);
    const stamps = unansweredCardPushes.get(mutationId);
    if (!stamps) return false;
    const keys = Object.keys(stamps);
    const attempt =
      1 + Math.max(0, ...keys.map((k) => failedCardPushAttempts.get(k) ?? 0));
    if (attempt > CARD_PUSH_RETRIES) return settleCardPush(mutationId);
    for (const key of keys) failedCardPushAttempts.set(key, attempt);
    unansweredCardPushes.delete(mutationId);
    // Its decks stay marked: an app killed before the retry re-sends at launch.
    unansweredDeckMarks.delete(mutationId);
    hasDeferredLocalWrites = true;
    if (!cardPushRetryTimer) {
      cardPushRetryTimer = setTimeout(() => {
        cardPushRetryTimer = null;
        // Not ready: the next handshake reconciles the deferred write.
        if (syncStatusRef.current === 'ready') void reconcileDeferredLocalWrites();
      }, CARD_PUSH_RETRY_MS * attempt);
    }
    return true;
  };
  const holdUntilAnswered = (held: HeldCardPushes): void => {
    const tracker = getUnsyncedDeckTracker();
    for (const [mutationId, stamps] of held) {
      unansweredCardPushes.set(mutationId, stamps);
      const decks = new Set<string>();
      for (const tsKey of Object.keys(stamps)) {
        // `__card\x01{deckId}\x01{cardId}`
        const parts = tsKey.split(CARD_KEY_DELIM);
        if (parts.length >= 3) decks.add(parts[1]!);
      }
      for (const deckId of decks) tracker.markUnanswered(deckId);
      unansweredDeckMarks.set(
        mutationId,
        tracker.capture((id) => decks.has(id)),
      );
    }
  };
  const inFlightStamps = (): Record<string, string> =>
    Object.assign({}, ...unansweredCardPushes.values());
  const reconcileDeferredLocalWrites = async (): Promise<void> => {
    if (!hasDeferredLocalWrites) return;
    hasDeferredLocalWrites = false;
    try {
      await flushOfflineEdits(ctx, holdUntilAnswered, inFlightStamps());
    } catch (err) {
      // Never fail the handshake over this: the write is still in the
      // store and `serverTsRef` was not advanced for it, so the next
      // connection's `flushLocal` picks it up.
      hasDeferredLocalWrites = true;
      console.error('[Sync] deferred local-write flush failed:', err);
    }
  };

  // ── Sync-init watchdog ─────────────────────────────────────────────
  // The init handshake has no failure signal from the server: if the DO
  // dies mid-`buildSyncDiff` (large-account OOM) the reply simply never
  // arrives, the status stays 'syncing' forever, and every UI gate that
  // waits for the first sync (dashboard onboarding gate, "Syncing your
  // changes…" banner) hangs indefinitely — even though the local store
  // holds perfectly usable data. Arm a timer when we enter the init
  // flow; if neither `sync-in-sync` nor `sync-diff` lands in time, fail
  // over to 'error' so the app falls back to local data.
  //
  // Recovery: re-entry requires a fresh `sync-state: ready`, which the
  // server sends per-connect — so it's automatic when the DO died (CF
  // closes the socket → y-partyserver reconnects → re-arm). A DO that
  // hangs while keeping the socket OPEN stays at 'error' until something
  // closes the socket (navigation, the visibility-grace teardown);
  // forcing a `ws.close()` when the watchdog fires would turn that into
  // a true retry loop but needs provider access here — follow-up.
  let syncInitWatchdog: ReturnType<typeof setTimeout> | null = null;
  const clearSyncInitWatchdog = () => {
    if (syncInitWatchdog !== null) {
      clearTimeout(syncInitWatchdog);
      syncInitWatchdog = null;
    }
  };
  const armSyncInitWatchdog = () => {
    clearSyncInitWatchdog();
    syncInitWatchdog = setTimeout(() => {
      syncInitWatchdog = null;
      if (syncStatusRef.current === 'syncing') {
        console.warn(
          `[Sync] no sync-init reply within ${SYNC_INIT_TIMEOUT_MS}ms — falling back to local data`,
        );
        ctx.setSyncStatus('error');
      }
    }, SYNC_INIT_TIMEOUT_MS);
    // Node-only (tests): don't let a pending watchdog hold the process
    // open. Browsers/RN return a number here — optional chain no-ops.
    (syncInitWatchdog as { unref?: () => void }).unref?.();
  };

  // ── Migration retry ────────────────────────────────────────────────
  // A `migration-error` (the legacy export has not landed yet, R2 was
  // unreachable, the export object is broken) used to be terminal for
  // as long as the socket stayed open: nothing re-sent `request-migration`,
  // so the user sat behind "Something went wrong. Retrying…" until a
  // reload or a network drop forced a reconnect (whose `needs-seed` asks
  // again). Now the adapter re-asks by itself on a capped, jittered
  // backoff. The wait is a SEED state (`waiting`, `account-seed.ts`), not a
  // sync status: `syncStatus` stays 'connecting' — the socket is open, the
  // sync handshake simply has not started. The DO broadcasts `migrating` only once a run really
  // starts reading the export. The UserDo checks the readiness gate BEFORE
  // that broadcast (`runLegacyMigration`), so while the gate is closed —
  // the long wait this retry exists for — a re-ask answers
  // `export-not-ready` with no `migrating`: no overlay flicker, no
  // Migration-Started/Failed pair per retry. A run that starts and then
  // fails (R2 read error) does flip through `migrating`, once per attempt.
  // Cancelled by any other `sync-state`, a disconnect (the reconnect's
  // `needs-seed` re-asks) and unbind.
  let migrationRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let migrationRetryAttempt = 0;
  const cancelMigrationRetry = () => {
    if (migrationRetryTimer !== null) {
      clearTimeout(migrationRetryTimer);
      migrationRetryTimer = null;
    }
  };
  const scheduleMigrationRetry = (slow: boolean) => {
    cancelMigrationRetry();
    const delay = migrationRetryDelayMs(
      migrationRetryAttempt,
      Math.random,
      slow ? MIGRATION_RETRY_SLOW_MAX_MS : MIGRATION_RETRY_MAX_MS,
    );
    migrationRetryAttempt += 1;
    console.warn(
      `[Sync] re-requesting migration in ${delay}ms (attempt ${migrationRetryAttempt})`,
    );
    migrationRetryTimer = setTimeout(() => {
      migrationRetryTimer = null;
      ctx.sendRef.current({ type: 'request-migration' });
    }, delay);
    (migrationRetryTimer as { unref?: () => void }).unref?.();
  };
  // Same timer slot (so every cancel above covers it): while seeding, a
  // stretch of silence re-asks, and keeps re-asking — see SEED_STALL_REASK_MS.
  const armSeedStallReask = () => {
    cancelMigrationRetry();
    migrationRetryTimer = setTimeout(() => {
      migrationRetryTimer = null;
      console.warn(`[Sync] seed silent for ${SEED_STALL_REASK_MS}ms — asking again`);
      ctx.sendRef.current({ type: 'request-migration' });
      armSeedStallReask();
    }, SEED_STALL_REASK_MS);
    (migrationRetryTimer as { unref?: () => void }).unref?.();
  };

  return {
    onProtocolMessage: async (raw, send) => {
      // Bridge the engine's `send(rawJson)` into the existing `ctx.sendRef`
      // that takes typed messages. Updating per call is cheap and lets
      // the existing operations call sendRef.current({...}).
      ctx.sendRef.current = (m: SyncClientMessage) => send(JSON.stringify(m));

      const msg = raw as UserDoServerMessage;

      if (msg.type === 'activity') {
        setActivityData(msg.data);
        return true;
      }

      const sync = msg as SyncServerMessage;

      // ── Handshake / migration lifecycle ──────────────────────────────
      if (sync.type === 'sync-state') {
        console.log('[Sync] state:', sync.status);
        // Any answer supersedes a pending re-ask; the error branch below
        // re-arms it when the new answer is again an error.
        cancelMigrationRetry();
        // Seed lifecycle: the socket is open and the handshake is held until
        // the DO has an account to sync, so the CONNECTION reads
        // 'connecting' throughout; what the user is waiting on is told by
        // the seed state, never by `syncStatus`.
        if (sync.status === 'needs-seed') {
          // Server-driven migration: ask the DO to seed itself from the
          // legacy export. The DO will stream `migrating` updates then
          // either `ready` or `migration-error`.
          ctx.setSyncStatus('connecting');
          onSeedEvent?.({ type: 'needs-seed' });
          ctx.sendRef.current({ type: 'request-migration' });
          armSeedStallReask();
          return true;
        }
        if (sync.status === 'migrating') {
          // Progress event from the DO mid-migration; progress is
          // best-effort and currently not surfaced beyond a console log.
          ctx.setSyncStatus('connecting');
          onSeedEvent?.({ type: 'migrating' });
          armSeedStallReask();
          console.log(
            `[Sync] migration phase=${sync.phase}`,
            sync.progress ? `progress=${JSON.stringify(sync.progress)}` : '',
          );
          return true;
        }
        if (sync.status === 'migration-error') {
          // A retryable refusal (`export-not-ready`, the lock) is the
          // expected WAIT, not a fault: warn, so error channels stay clean.
          // A non-retryable one is a fault on OUR side (the DO has already
          // reported it, naming the user): error — but the user still waits.
          (sync.retryable ? console.warn : console.error)(
            `[Sync] migration error (retryable=${sync.retryable}):`,
            sync.reason,
          );
          // Not a sync error: `waiting` on the seed state, and re-asked
          // below — always; slower when the DO says retrying cannot help
          // yet. A real sync failure after the seed still lands on 'error'
          // through the paths below.
          ctx.setSyncStatus('connecting');
          onSeedEvent?.({
            type: 'migration-error',
            reason: sync.reason,
            retryable: sync.retryable,
          });
          scheduleMigrationRetry(!sync.retryable);
          return true;
        }
        // status === 'ready'
        migrationRetryAttempt = 0;
        onSeedEvent?.({ type: 'ready' });
        ctx.setSyncStatus('syncing');
        // Until this handshake answers, the memberships in the store are the
        // disk's — no group mirror may be minted off them.
        useFlashkaStore.setState({ membershipsConfirmed: false });
        armSyncInitWatchdog();
        await performSyncInit(ctx);
        return true;
      }

      // ── Legacy notes bundle ──────────────────────────────────────────
      if (sync.type === 'legacy-notes-bundle') {
        // Platform-specific stash (web: Dexie; native: undefined → no-op).
        // ALWAYS ack regardless of stash outcome — if the client's local
        // storage is full or unavailable, we'd rather lose the legacy
        // notes than have the DO hold them indefinitely. The 30-day GC
        // is a backstop, not a primary failure mode.
        try {
          if (userId && legacyClient) {
            await legacyClient.stashLegacyNotes(userId, sync.deckNotes);
          }
        } catch (err) {
          console.warn('[Sync] stashLegacyNotes failed:', err);
        }
        ctx.sendRef.current({ type: 'legacy-notes-stashed-ack' });
        return true;
      }

      // The server unions card history only when it says so, and this
      // device's cards merge must do the same: folding a peer's row with a
      // union while the server keeps whole-row LWW would bring back an
      // installed build's Undo. Off until a server says on.
      if (sync.type === 'sync-features') {
        setCardHistoryJoinDisabled(sync.historyMerge !== true);
        return true;
      }

      // ── Version-cursor fast path ──────────────────────────────────────
      // Server confirmed our `lastVersion` matches its commit version, so
      // nothing changed server-side. Flush any edits made while disconnected
      // (the diff path's flushLocal is skipped here), persist the version, and
      // go ready — no diff, no full key set.
      if (sync.type === 'sync-in-sync') {
        clearSyncInitWatchdog();
        // The watchdog is disarmed, so a failure below must not strand
        // the status at 'syncing' — that would recreate the exact hang
        // this flow guards against, just one await later (e.g. a local
        // storage write rejecting). Fail over to 'error' instead; the
        // next reconnect redoes the handshake.
        try {
          const v = (sync as { version?: number }).version;
          if (typeof v === 'number') {
            ctx.serverVersionRef.current = v;
            await ctx.saveServerVersion(v);
          }
          await flushOfflineEdits(ctx, holdUntilAnswered, inFlightStamps());
        } catch (err) {
          console.error('[Sync] sync-in-sync handling failed:', err);
          ctx.setSyncStatus('error');
          return true;
        }
        ctx.setSyncStatus('ready');
        ctx.hasCompletedInitialSyncRef.current = true;
        // In sync: the cached memberships ARE the server's.
        useFlashkaStore.setState({ membershipsConfirmed: true });
        // `flushOfflineEdits` above ran BEFORE the gate opened, so a write
        // that landed during it is still held back. Reconcile after the
        // flip, not instead of it.
        await reconcileDeferredLocalWrites();
        return true;
      }

      // Versions diverged — the server wants our full timestamp manifest so it
      // can compute a precise diff.
      if (sync.type === 'sync-request-manifest') {
        // Fresh window: the full-manifest diff is the expensive server
        // path, so give it its own timeout budget.
        armSyncInitWatchdog();
        sendFullManifest(ctx);
        return true;
      }

      // A committed write advanced the DO's commit version (ours or another
      // tab's / a cross-DO fan-out). Persist it so the next reconnect can take
      // the version-only fast path instead of re-uploading the full manifest.
      // Monotonic — never regress. (This is what keeps the cursor current
      // after an edit; without it, any write strands the client one version
      // behind and forces a full-manifest resync on reconnect.)
      if (sync.type === 'sync-version') {
        const v = (sync as { version?: number }).version;
        if (
          typeof v === 'number' &&
          (ctx.serverVersionRef.current == null || v > ctx.serverVersionRef.current)
        ) {
          ctx.serverVersionRef.current = v;
          // The cursor certifies every frame before it, so those reach disk
          // first (same invariant as `handleSyncDiff`): a tab closing in
          // between otherwise reloads onto `sync-in-sync` over stale rows.
          drainPendingBulk();
          await getSyncStorage().flush?.();
          await ctx.saveServerVersion(v);
        }
        return true;
      }

      if (sync.type === 'sync-diff') {
        // Reply arrived — stop the watchdog BEFORE the (potentially slow)
        // apply so a big diff can't be misread as a dead server.
        clearSyncInitWatchdog();
        console.log(
          '[Sync] diff: cards=',
          Object.keys(sync.cards ?? {}).length,
          'deckProperties=',
          Object.keys(sync.deckProperties ?? {}).length,
        );
        // With the watchdog disarmed, a CLIENT-side apply/persist failure
        // (IndexedDB quota on a large diff, a teardown race closing the
        // DB) must fail over rather than leave the status 'syncing'
        // forever — the engine's dispatch has no rejection path of its
        // own. 'error' releases the UI to local data; timestamps that
        // didn't persist just widen the next reconnect's diff (safe).
        try {
          const perfT0 = perfNow(); // [perf-probe]
          await handleSyncDiff(sync, ctx, holdUntilAnswered);
          perfLog('sync-diff apply total', perfT0, () => ({
            cards: Object.keys(sync.cards ?? {}).length,
          }));
        } catch (err) {
          console.error('[Sync] sync-diff apply failed:', err);
          ctx.setSyncStatus('error');
          return true;
        }
        ctx.setSyncStatus('ready');
        useFlashkaStore.setState({ membershipsConfirmed: true });
        // The diff's own `flushLocal` was computed from the snapshot
        // `applySyncDiffPure` took at the top of `handleSyncDiff`. Anything
        // the user wrote while the apply was in flight — seconds, on a large
        // account — is not in it, and the gate above held those pushes back.
        await reconcileDeferredLocalWrites();
        return true;
      }

      // ── Server error envelope ─────────────────────────────────────────
      // UserDo emits `sync-error` via the framework `sendError` on the
      // non-mutation failure paths (rate-limit, invalid payload, auth,
      // internal error). Mirror the SG adapter's `sg-sync-error` so these
      // surface instead of falling through to the engine's dev-only
      // "unhandled message" warn. Mutation-bearing failures use
      // `op-rejected` (handled in the engine), not this wire.
      if (sync.type === 'sync-error') {
        console.warn('[Sync] server error:', sync.message);
        // A server error DURING the init handshake means the diff isn't
        // coming (e.g. `handleSyncInit` threw on a large account). Fail
        // over to 'error' so the UI releases its wait-for-first-sync
        // gates and renders local data instead of spinning forever; the
        // next reconnect retries the handshake.
        if (syncStatusRef.current === 'syncing') {
          clearSyncInitWatchdog();
          ctx.setSyncStatus('error');
        }
        return true;
      }

      // ── Entity broadcasts (registry-driven) ───────────────────────────
      const entity = clientBroadcastMap[sync.type];
      if (entity) {
        // Bulk wires first — server emits one bulk frame per chunk for
        // record entities since commit 205273b.
        if (sync.type === entity.bulkBroadcastType && entity.applyServerBulkBroadcast) {
          await entity.applyServerBulkBroadcast(sync, ctx);
          return true;
        }
        if (
          sync.type === entity.bulkDeleteBroadcastType &&
          entity.applyServerBulkDeleteBroadcast
        ) {
          await entity.applyServerBulkDeleteBroadcast(sync, ctx);
          return true;
        }
        if (
          sync.type === entity.deleteBroadcastType &&
          entity.applyServerDeleteBroadcast
        ) {
          await entity.applyServerDeleteBroadcast(sync, ctx);
          return true;
        }
        await entity.applyServerBroadcast(sync, ctx);
        return true;
      }

      // Aggregate `linked-cards-updated` — used by the linked-deck
      // propagation path to fan out a card batch to all connected tabs
      // at once. Bespoke because it carries a full card array, not a
      // per-row entry.
      if (sync.type === 'linked-cards-updated') {
        const s = sync as Extract<SyncServerMessage, { type: 'linked-cards-updated' }>;
        await applyAggregateLinkedCards(s, ctx);
        return true;
      }

      // Cold-deck rehydrate completion. The bulk frames have already
      // been applied above; this just unblocks any awaiter (e.g. the
      // share-to-group flow) so it can read the freshly-resident cards.
      if (sync.type === 'deck-cards-loaded') {
        const s = sync as Extract<SyncServerMessage, { type: 'deck-cards-loaded' }>;
        // [perf-probe] end of the cold-deck rehydrate stream — pair with the
        // "request-deck-cards sent" mark to get the full stream window.
        perfMark('deck-cards-loaded (rehydrate stream done)', () => ({
          deckId: s.deckId,
        }));
        // The stream's frames are staged for a coalesced publish; land them
        // NOW, synchronously, so residency is judged against the full bucket
        // and every `awaitDeckRehydrated` caller reads the cards it waited
        // for. Handlers are dispatched un-serialised — nothing here awaits.
        drainPendingBulk();
        // The stream delivered the deck's full set: the loader owns the
        // residency flags (the deck open only flipped them optimistically).
        // A SERVER stream says nothing about this device's rows — for an
        // archived deck opened online, disk was skipped on purpose — so it
        // does not claim "memory ⊇ disk" (`diskLoadedDeckIds`).
        useFlashkaStore.getState().markDeckCardsLoaded(s.deckId, { coversDisk: false });
        notifyDeckRehydrated(s.deckId);
        return true;
      }

      return false;
    },

    subscribeDirty: (send) => {
      // Bridge typed sends through the engine's raw `send` callback.
      ctx.sendRef.current = (m: SyncClientMessage) => send(JSON.stringify(m));

      let scheduled = false;
      let capturedPrev: FlashkaState | null = null;

      const flush = () => {
        scheduled = false;
        const prev = capturedPrev;
        capturedPrev = null;
        if (!prev) return;

        // Only push when the lifecycle is in `ready`. The sendRef guard
        // (ws.OPEN check inside the engine's `send`) is authoritative
        // even if we slip; this is a coarse early exit.
        //
        // DEFER, never drop: the captured snapshot is useless once the
        // handshake has rewritten the store under it, but the fact that
        // something local went unpushed is not. `reconcileDeferredLocalWrites`
        // re-derives it from the store when the status reaches `ready`.
        // Nothing is stamped into `serverTsRef` here, so a tab that never
        // gets there still has the write recovered by the next
        // connection's `flushLocal`.
        if (syncStatusRef.current !== 'ready') {
          hasDeferredLocalWrites = true;
          return;
        }

        const state = useFlashkaStore.getState();
        const serverTimestamps = ctx.serverTsRef.current;
        const prevSnap = buildSnapshot(prev, serverTimestamps);
        const nextSnap = buildSnapshot(state, serverTimestamps);
        // `delta` holds only the timestamps this tick advanced; the live map is
        // read through, never copied (it has one entry per library card).
        const { messages, updatedTimestamps: delta } = detectDirtyChangesDelta(
          prevSnap,
          nextSnap,
        );
        // A deck deleted while its cards were never resident has no bucket to
        // diff, so nothing above says "delete these rows" (see the helper).
        messages.push(
          ...cardDeletesForNonResidentDeckRemovals(prev, state, serverTimestamps, delta),
        );
        if (messages.length === 0) return;
        // Card pushes carry a `mutationId`; their stamps wait for its answer.
        const held = holdCardPushStamps(messages, delta, {});
        let allDelivered = true;
        for (const msg of messages) {
          if (!ctx.sendRef.current(msg)) allDelivered = false;
        }
        // Advance the synced cursor only for a push that actually LEFT this
        // device. The engine's `send` returns false when the socket is not
        // OPEN and drops the message with no error, and an edit made between
        // socket loss and `setSyncStatus('disconnected')` lands exactly in
        // that window. Stamping it synced made every later flush skip the row
        // (`entryTs <= serverTs`), so the edit existed on this device only,
        // for ever. Leaving the cursor behind costs one redundant re-push —
        // the same `allDelivered` discipline `handleSyncDiff` and
        // `flushOfflineEdits` already apply to the unsynced-deck marks.
        if (!allDelivered) return;
        holdUntilAnswered(held);
        // Nothing but held card stamps this tick: nothing to record yet.
        if (Object.keys(delta).length === 0) return;
        // Advanced IN PLACE, like every broadcast handler does: equal to the
        // old `{ ...live, ...delta }` swap (the diff never deletes a key), and
        // every snapshot that needs isolation (`applySyncDiff`, sync-init)
        // already copies the live map.
        const live = ctx.serverTsRef.current;
        Object.assign(live, delta);
        void ctx.saveServerSyncedTs(live);
      };

      const unsub = useFlashkaStore.subscribe((_state, prevState) => {
        if (getMutationSource() === 'server') return;
        if (scheduled) return;
        capturedPrev = prevState;
        scheduled = true;
        queueMicrotask(flush);
      });

      return () => {
        unsub();
        capturedPrev = null;
        scheduled = false;
      };
    },

    onDispose: () => {
      // Engine unbind (sign-out, user switch, provider swap). Without
      // this, an armed watchdog outlives the adapter and can fire a
      // stale status transition into the hook-shared ctx/refs later.
      // Timer-only cleanup — no status change: on unmount the update
      // would be dropped anyway, and on a provider swap the next
      // binding owns the status lifecycle.
      clearSyncInitWatchdog();
      clearCardPushRetry();
      cancelMigrationRetry();
      // The tracker's flag is NOT the sync status, and this is the one place
      // that difference bites: it means "a live socket is pushing my edits
      // right now", which unbind ends. `onDisconnected` cannot say it — the
      // engine detaches `connection-close` BEFORE disposing — so without this
      // the flag stayed `true` for the life of the JS context after the first
      // successful connect. Every later offline card edit then took the
      // `if (syncReady) return` path in `mark()`, was never written to
      // `settings/unsyncedDeckIds`, was not force-loaded at the next launch,
      // and so was never pushed: the edit existed only on that device.
      getUnsyncedDeckTracker().setSyncReady(false);
      // Same for a study-group mirror mint: the next binding has not
      // reconciled this store yet (`internal/personal-sync-ready.ts`).
      setPersonalSyncReady(false);
      // `connection-close` no longer reaches `onDisconnected` after unbind;
      // land any staged frames here so they do not publish into whatever
      // binding comes next.
      drainPendingBulk();
    },

    onAuthRejected: (code, reason) => {
      // The engine already stopped the reconnect loop (threshold of
      // consecutive 4401/4403 closes — see AUTH_CLOSE_TERMINAL_THRESHOLD).
      // Local data stays usable; recovery is a fresh sign-in (new
      // provider with new credentials). Report loudly: a fleet-wide
      // spike of this event is the primary "auth cutover broke sync"
      // alarm, and HTTP-side 401s are filtered from Sentry on mobile.
      ctx.setSyncStatus('error');
      console.error(`[Sync] server rejected credentials (${code}) — sync halted`);
      if (!authRejectionReported) {
        authRejectionReported = true;
        Sentry.captureMessage('sync auth rejected: UserDo closed with auth code', {
          level: 'error',
          tags: { sync_scope: 'user-do', ws_close_code: String(code) },
          extra: { ...(reason ? { ws_close_reason: reason } : {}) },
        });
      }
    },
    onPushAck: settleCardPush,
    onPushRejected: rejectCardPush,

    onDisconnected: () => {
      // Frames received before the drop are on disk; land them in the store
      // too so nothing waits on an animation frame that a backgrounded
      // runtime may not deliver.
      drainPendingBulk();
      // Socket gone — the reply this watchdog guards can't arrive on it.
      // The reconnect's `sync-state: ready` re-arms.
      clearSyncInitWatchdog();
      clearCardPushRetry();
      // Their answers can no longer arrive; the next handshake re-sends.
      unansweredCardPushes.clear();
      // Their decks stay marked: the next handshake's flush re-sends the rows
      // and clears the marks it covers.
      unansweredDeckMarks.clear();
      // Same for a pending migration re-ask: the reconnect's `needs-seed`
      // asks again on its own.
      cancelMigrationRetry();
      ctx.setSyncStatus('disconnected');
      // hasCompletedInitialSyncRef intentionally preserved across
      // disconnects — once we've seen the server's truth this session,
      // a transient WebSocket drop doesn't invalidate that. The hook's
      // currentUserId-driven reset (see use-user-do-sync.ts) handles
      // sign-out / user-switch. Keeping the ref stable here matches the
      // useState mirror so UI gates (e.g. dashboard's "wait for first
      // sync" guard) don't re-trigger on every visibility-grace
      // reconnect.
    },
  };
}

// ---------------------------------------------------------------------------
// Sync-init: send the handshake with locally-known timestamps
// ---------------------------------------------------------------------------

async function performSyncInit(ctx: SyncCtx): Promise<void> {
  // Wait for the persisted server-timestamp map + version to land in their
  // refs. Without this gate, a reconnect can fire before the
  // Dexie/AsyncStorage read resolves, sending `{}` for a user with local data
  // and triggering a multi-MB sync-diff from the server.
  await ctx.serverTsLoadedRef.current;

  const version = ctx.serverVersionRef.current;
  const hasLocalData = Object.keys(ctx.serverTsRef.current).length > 0;

  // Version-only fast path: send just the cursor. Guarded on having BOTH a
  // persisted version AND local data — they share a storage tier, so a
  // cleared/evicted store has neither and we fall through to the full
  // manifest (= safe full resync). `supportsColdDecks` is included so even an
  // old server cold-filters the diff if it can't honor the cursor.
  if (version != null && hasLocalData) {
    const coldDecks = getSyncClientCapabilities().coldDecks;
    ctx.sendRef.current({
      type: 'sync-init',
      lastVersion: version,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      ...(coldDecks ? { supportsColdDecks: true } : {}),
    } as SyncClientMessage);
    return;
  }

  // Cold start / cleared store: send the full timestamp manifest.
  sendFullManifest(ctx);
}

/**
 * Send the full `sync-init` timestamp manifest — used on cold start and as the
 * phase-2 reply to the server's `sync-request-manifest`. Carries `lastVersion`
 * when known (harmless; the server takes the diff path whenever a manifest is
 * present).
 */
function sendFullManifest(ctx: SyncCtx): void {
  const snap = buildSnapshot(useFlashkaStore.getState(), { ...ctx.serverTsRef.current });
  const msg = buildSyncInitPure(snap);
  const version = ctx.serverVersionRef.current;
  const tsKeys = Object.keys(ctx.serverTsRef.current);
  console.log(
    `[Sync] performSyncInit (full manifest): serverTsRef.keys=${tsKeys.length} localDecks=${Object.keys(snap.deckProperties ?? {}).length}`,
  );
  const tzOffsetMinutes = new Date().getTimezoneOffset();
  ctx.sendRef.current(
    version != null
      ? ({ ...msg, lastVersion: version, tzOffsetMinutes } as SyncClientMessage)
      : ({ ...msg, tzOffsetMinutes } as SyncClientMessage),
  );
}

// ---------------------------------------------------------------------------
// Sync-diff: apply server response + push locally-newer rows
// ---------------------------------------------------------------------------

async function handleSyncDiff(
  diff: Extract<SyncServerMessage, { type: 'sync-diff' }>,
  ctx: SyncCtx,
  onHeld: (held: HeldCardPushes) => void = () => {},
): Promise<void> {
  // The snapshot below must include every frame already received: a pending
  // publish landing AFTER the diff would overwrite the diff's rows with the
  // frames' older ones, and the flush would judge local rows against a store
  // that lags disk.
  drainPendingBulk();
  const snapState = useFlashkaStore.getState();
  const snap = buildSnapshot(snapState, { ...ctx.serverTsRef.current });
  // Unsynced decks whose FULL row set is in this snapshot are covered by the
  // flushLocal pass below; captured now so an edit landing before the sends
  // keeps its mark (generation check in `clearCaptured`).
  const unsyncedCovered = captureUnsyncedCoveredBy(snapState);
  const result = applySyncDiffPure(diff, snap);
  // A deck reset on this device while offline, whose meta another device
  // changed meanwhile: the diff's row replaces ours. The store keeps our
  // floor on it; here it is re-stamped and pushed, or the server never
  // learns of the reset (see `unackedProgressFloorDecks`).
  const unackedFloors = unackedProgressFloorDecks(
    snapState,
    result.recordResults.deckProperties as
      | Record<string, FlashkaState['deckProperties'][string]>
      | undefined,
    ctx.serverTsRef.current,
  );

  // Persist-after-apply: the Zustand writes below run synchronously inside
  // `withServerSource`, but the local-storage writes are COLLECTED here and
  // drained in awaited chunks afterwards. The old path issued one
  // fire-and-forget `storage.put` per row inside the loop; for a large
  // initial diff that piled up ~40k in-flight IDB ops (the synchronous loop
  // never yields, so none could drain) and OOM'd the renderer the instant it
  // yielded at the first await. Collecting + chunked-await keeps only one
  // batch in flight. storage.put doesn't trigger the store subscription, so
  // moving it out of withServerSource is echo-safe.
  const storage = getSyncStorage();
  const pendingWrites = new Map<string, unknown[]>();
  const queueWrite = (table: string, row: unknown): void => {
    const existing = pendingWrites.get(table);
    if (existing) existing.push(row);
    else pendingWrites.set(table, [row]);
  };

  // Apply all Zustand mutations inside withServerSource to prevent echo.
  // The subscription fires synchronously on each set() and sees source="server".
  withServerSource(() => {
    const store = useFlashkaStore.getState() as unknown as Record<
      string,
      (data: unknown) => void
    >;
    for (const entity of clientEntities) {
      if (entity.type === 'record') continue;
      const data = result.genericsToApply[entity.stateKey];
      if (data === undefined) continue;
      store[entity.methodNames.set]?.(data);
    }
    // Record entities — generic dispatch by stateKey (collect rows to persist).
    // Nested records (`nested: true`, today: `cards`) carry a
    // `Record<parentId, Record<childPart, T>>` diff payload; merge one
    // level deeper so per-parent buckets don't clobber unmentioned
    // children. Flat records merge at the top level as before.
    for (const [stateKey, data] of Object.entries(result.recordResults)) {
      if (!data || Object.keys(data).length === 0) continue;
      const entity = clientEntityByStateKey[stateKey];
      if (!entity) continue;
      const state = useFlashkaStore.getState() as unknown as Record<string, unknown>;
      const current = (state[stateKey] as Record<string, unknown> | undefined) ?? {};

      if (entity.nested) {
        // For the cards entity (today the only nested record), apply the
        // same scrub every other card write gets (`cleanStoredCard` — the
        // disk-row twin of `deck-slice.cleanCardForStore`): coerce
        // optional-string fields whose runtime type drifted to array —
        // legacy rows with `notes: []` etc. otherwise re-enter the store
        // on every reconnect and trip `cards-bulk-push` validation on
        // the next outbound flush — AND backfill the personal arrays. This
        // was the one write into `cards` that skipped the backfill, so a
        // server row without `studyState` / `actions` (legacy, or `null`
        // in the diff) reached readers that do `.length` on them and tore
        // the deck page down (Sentry FLASHKA-VITE-2VZ). A no-op, same
        // reference, for entries that are already schema-shaped.
        const sanitize = stateKey === 'cards';
        const next = { ...current } as Record<string, Record<string, unknown>>;
        for (const [parentId, bucket] of Object.entries(
          data as Record<string, Record<string, unknown>>,
        )) {
          if (sanitize) {
            for (const [childKey, entry] of Object.entries(bucket)) {
              bucket[childKey] = cleanStoredCard(
                entry as Parameters<typeof cleanStoredCard>[0],
              );
            }
          }
          next[parentId] = {
            ...((current[parentId] as Record<string, unknown> | undefined) ?? {}),
            ...bucket,
          };
        }
        useFlashkaStore.setState({ [stateKey]: next } as Partial<FlashkaState>);
        const parentField = entity.parentKeyField;
        // Persist what the store HOLDS, not the diff's row: the store's write
        // guard may have cleaned it (progress-reset floor), and these chunked
        // writes land after the guard's own re-persist.
        const applied = (
          useFlashkaStore.getState() as unknown as Record<string, unknown>
        )[stateKey] as Record<string, Record<string, unknown> | undefined>;
        for (const [parentId, bucket] of Object.entries(
          data as Record<string, Record<string, unknown>>,
        )) {
          for (const [childKey, diffEntry] of Object.entries(bucket)) {
            const entry = applied[parentId]?.[childKey] ?? diffEntry;
            const row = parentField
              ? { ...(entry as Record<string, unknown>), [parentField]: parentId }
              : entry;
            queueWrite(entity.storageKey, row);
          }
        }
        continue;
      }

      useFlashkaStore.setState({
        [stateKey]: { ...current, ...data },
      } as Partial<FlashkaState>);
      const applied = (useFlashkaStore.getState() as unknown as Record<string, unknown>)[
        stateKey
      ] as Record<string, unknown>;
      for (const [key, diffEntry] of Object.entries(data as Record<string, unknown>)) {
        queueWrite(entity.storageKey, applied[key] ?? diffEntry);
      }
    }

    if (unackedFloors.length > 0) {
      const deckEntity = clientEntityByStateKey.deckProperties;
      const now = new Date().toISOString();
      const props = { ...useFlashkaStore.getState().deckProperties };
      for (const id of unackedFloors) {
        const local = snapState.deckProperties[id]!;
        const row = {
          ...props[id],
          progressResetAtISO: local.progressResetAtISO,
          // The reset cleared the exam plan in the same write; the diff's
          // newer row still carries the old plan (the plan has no clock of
          // its own). Same carry as the server's for a losing reset push.
          ...('examPlan' in local ? { examPlan: local.examPlan } : {}),
          updatedAtISO: now,
          personalUpdatedAtISO: now,
        };
        props[id] = row;
        queueWrite(deckEntity.storageKey, row);
        result.updatedTimestamps[deckEntity.tsKeyPrefix + id] = now;
        result.outboundMessages.push({
          type: deckEntity.pushType,
          key: id,
          entry: row,
          updatedAtISO: now,
        } as unknown as (typeof result.outboundMessages)[number]);
      }
      useFlashkaStore.setState({ deckProperties: props });
    }
  });

  // Cold-deck cache: a cold-deck-aware diff carries the full per-deck activity
  // map. Persist it (so the next cold-start hydration computes warm/cold
  // offline) and recompute which decks are archived. Warm decks' card bodies
  // arrive next as `cards-bulk-updated` frames; cold decks render archived.
  const coldDiff = diff as { deckActivity?: Record<string, string> };
  if (coldDiff.deckActivity) {
    const deckActivity = coldDiff.deckActivity;
    void storage.put('settings', { key: 'deckActivityMap', value: deckActivity });
    const store = useFlashkaStore.getState();
    const { warm, archived } = partitionDecksByWarmth(
      Object.keys(store.deckProperties),
      deckActivity,
      Date.now(),
    );
    // "Warm" means the SERVER keeps this deck's cards flowing — NOT that this
    // client has them in RAM. A deck warmed elsewhere (another tab/device, or
    // a rehydrate right before a reload) is warm while this session never
    // received its bodies (the diff only carries cards NEWER than our cursor).
    // Claiming such a deck "loaded" would suppress the open-deck rehydrate
    // and render it permanently empty — so only count a warm deck as loaded
    // when its cards are actually resident in the store.
    //
    // Under lazy card hydration a warm deck's bodies sit on DISK until a
    // loader reads them, and a non-empty bucket may be one frame of many — so
    // the flag is owned by the loaders (disk, `deck-cards-loaded`, whole-deck
    // writes): keep what they set, drop only what went cold.
    const loaded = new Set<string>();
    if (getSyncClientCapabilities().lazyCardHydration) {
      for (const id of store.loadedDeckIds) if (warm.has(id)) loaded.add(id);
    } else {
      const residentCards = store.cards;
      for (const id of warm) {
        const bucket = residentCards[id];
        if (bucket && Object.keys(bucket).length > 0) loaded.add(id);
      }
    }
    store.setColdDeckState({
      deckActivity,
      loadedDeckIds: loaded,
      archivedDeckIds: archived,
    });
  }

  // Drain the collected writes in awaited chunks — bounded memory, lets the
  // storage layer (IDB on web) commit + free each batch before the next.
  await flushPendingWrites(storage, pendingWrites);

  // …AND the writes nobody collected. `pendingWrites` holds RECORD entities
  // only; every GENERIC entity (folders, settings, userInfo, todos…) persisted
  // itself inside `withServerSource` above, through a fire-and-forget
  // `persistReplace`. On web that is `clear()` + one put per row in a single
  // Dexie transaction — hundreds of awaits for a big `folders` collection —
  // and nothing downstream waited for it while BOTH checkpoints below were
  // awaited. A navigation landing in that window (a teardown deleting a deck
  // and moving on, a user clicking through) aborts the transaction with the
  // table already CLEARED, and the cursor is then saved claiming data that is
  // no longer there.
  //
  // The damage is not transient, which is why this is worth an await: the next
  // load takes the version fast path on that cursor, the server answers
  // `sync-in-sync` and sends no diff at all, so the client shows ZERO folders
  // and never asks again — until some other client happens to touch a folder.
  // Measured 2026-09-17 on the scenario teardown: `folders=0 decks=983
  // idbFolders=0`, live rows sitting in the UserDo the whole time.
  //
  // THE INVARIANT: a checkpoint is never persisted ahead of the data it
  // certifies. The comment below says so for the version; the timestamps and
  // the generic writes are the same promise.
  await storage.flush?.();

  // The card pushes this pass sends are recorded on the server's answer.
  const held = holdCardPushStamps(
    result.outboundMessages,
    result.updatedTimestamps,
    ctx.serverTsRef.current,
  );
  ctx.serverTsRef.current = result.updatedTimestamps;
  await ctx.saveServerSyncedTs(result.updatedTimestamps);

  // Persist the commit version LAST — strictly after the data + timestamps —
  // so a crash mid-apply can only leave the version BEHIND the applied state
  // (→ a safe redundant full diff next reconnect), never ahead of it (which
  // would let the fast path skip un-applied data).
  const diffVersion = (diff as { version?: number }).version;
  if (typeof diffVersion === 'number') {
    ctx.serverVersionRef.current = diffVersion;
    await ctx.saveServerVersion(diffVersion);
  }

  let allDelivered = true;
  for (const msg of result.outboundMessages) {
    if (!ctx.sendRef.current(msg)) allDelivered = false;
  }
  if (allDelivered) onHeld(held);
  // Only retire a deck's mark once its push actually LEFT this device. A
  // closed socket drops the message with no error, and the mark is what makes
  // the next launch load that deck's rows at all — clear it on a dropped push
  // and the offline edit is stranded on disk for ever, because `flushLocal`
  // walks resident rows only. Keeping it costs one redundant force-load.
  if (allDelivered) getUnsyncedDeckTracker().clearCaptured(unsyncedCovered);

  ctx.hasCompletedInitialSyncRef.current = true;
}

/**
 * The marked decks this flush covers: those whose every LOCAL row is in memory
 * (`flushLocal` walks resident rows, so an edit on disk only would be missed).
 * "Memory ⊇ disk" is `diskLoadedDeckIds` — set by the disk loader and the
 * stage-1 force-load — or a complete bucket. Not `deckBucketComplete` alone:
 * that is false for an ARCHIVED deck by definition, and a deck re-archived by a
 * diff mid-session (the server has not seen its activity bump yet) and edited
 * offline again would keep its mark, and its per-boot force-load, until the
 * server warmed it — even though the flush did push its rows.
 */
function captureUnsyncedCoveredBy(state: FlashkaState): ReadonlyMap<string, number> {
  return getUnsyncedDeckTracker().capture(
    (deckId) => state.diskLoadedDeckIds.has(deckId) || deckBucketComplete(state, deckId),
  );
}

// ---------------------------------------------------------------------------
// Offline-edit flush (version-cursor fast path)
// ---------------------------------------------------------------------------

/** Card pushes held for the server's answer: `mutationId` → the synced
 *  timestamps the push would record. */
type HeldCardPushes = Array<[mutationId: string, stamps: Record<string, string>]>;

/**
 * Tag the card pushes among `messages` with a `mutationId` and take their
 * timestamps out of `next` (back to `prev`'s value, or absent), so the cursor
 * records them only on the server's answer. The rest of `next` is untouched.
 */
function holdCardPushStamps(
  messages: readonly SyncClientMessage[],
  next: Record<string, string>,
  prev: Record<string, string>,
): HeldCardPushes {
  const held: HeldCardPushes = [];
  for (const msg of messages) {
    const m = msg as { type: string; key?: string; entries?: Array<{ key?: string }> };
    if (m.type !== 'cards-push' && m.type !== 'cards-bulk-push') continue;
    const stamps: Record<string, string> = {};
    for (const key of m.entries ? m.entries.map((e) => e.key) : [m.key]) {
      if (!key) continue;
      const tsKey = `__card${CARD_KEY_DELIM}${key}`;
      if (next[tsKey] === undefined || next[tsKey] === prev[tsKey]) continue;
      stamps[tsKey] = next[tsKey]!;
      if (prev[tsKey] === undefined) delete next[tsKey];
      else next[tsKey] = prev[tsKey]!;
    }
    const mutationId = uuid();
    (msg as { mutationId?: string }).mutationId = mutationId;
    held.push([mutationId, stamps]);
  }
  return held;
}

/**
 * Push edits made while disconnected WITHOUT a server diff. On the
 * `sync-in-sync` fast path the server confirmed our version is current, so its
 * key set equals our `serverTsRef` key set. We synthesize a keys-only diff and
 * run the normal `applySyncDiff` flush: record entities push only rows whose
 * local ts is newer than what we last synced (no resurrection — every synced
 * key is in `serverKeys`); collections/values push only items past their
 * last-synced checkpoint. Behaviorally identical to a normal reconnect whose
 * server diff happened to be empty.
 */
async function flushOfflineEdits(
  ctx: SyncCtx,
  onHeld: (held: HeldCardPushes) => void = () => {},
  inFlight: Record<string, string> = {},
): Promise<void> {
  drainPendingBulk(); // same reason as in `handleSyncDiff`
  const snapState = useFlashkaStore.getState();
  // Rows already pushed and awaiting their answer count as sent here (not
  // re-pushed); they are never saved as synced on that account (below).
  const real = ctx.serverTsRef.current;
  const snap = buildSnapshot(snapState, { ...real, ...inFlight });
  const unsyncedCovered = captureUnsyncedCoveredBy(snapState);
  const syntheticDiff = buildServerKeysOnlyDiff(ctx.serverTsRef.current);
  const result = applySyncDiffPure(
    syntheticDiff as Extract<SyncServerMessage, { type: 'sync-diff' }>,
    snap,
  );
  const held = holdCardPushStamps(
    result.outboundMessages,
    result.updatedTimestamps,
    real,
  );
  for (const [key, ts] of Object.entries(inFlight)) {
    if (result.updatedTimestamps[key] !== ts) continue;
    if (real[key] === undefined) delete result.updatedTimestamps[key];
    else result.updatedTimestamps[key] = real[key]!;
  }
  let allDelivered = true;
  for (const msg of result.outboundMessages) {
    if (!ctx.sendRef.current(msg)) allDelivered = false;
  }
  if (allDelivered) onHeld(held);
  // Cleared only on DELIVERY, not on enqueue. The reconnect flush re-walks
  // every resident row, so a dropped push is usually harmless — but the marks
  // are also what force-load a deck's rows at the next launch, and a device
  // killed before that reconnect would never make the deck resident again.
  if (allDelivered) getUnsyncedDeckTracker().clearCaptured(unsyncedCovered);
  if (result.outboundMessages.length === 0) return;
  // Persist any timestamp advances the flush stamped (mirrors handleSyncDiff).
  ctx.serverTsRef.current = result.updatedTimestamps;
  await ctx.saveServerSyncedTs(result.updatedTimestamps);
}

/**
 * `cards-bulk-delete` frames for a deck deleted while its card bodies were NOT
 * resident.
 *
 * Deleting a deck tombstones its meta (which goes out as a
 * `deckProperties-bulk-push`) and drops the bucket. The dirty scan turns that
 * dropped bucket into `cards-bulk-delete` for every row it held — but under
 * lazy card hydration a deck the user never opened has NO bucket, and the only
 * other wire the server cascades card rows on is a deckProperties DELETE
 * (`userdo.ts` afterHandled: `if (!isDelete) return`). So the rows vanished
 * here and lived for ever there: a fresh device downloaded them back, exports
 * carried them, and re-creating the id resurrected the deck.
 *
 * The keys come from `serverTsRef` — exactly the rows this device knows the
 * server holds for that deck — so a non-resident delete emits the same wires a
 * resident one already does. Each key is stamped into `ts` like the framework's
 * own delete path, so the next flush does not re-send it.
 */
const CARD_KEY_DELIM = '\x01';
/** Re-sends of a card push the server keeps failing on, before it is settled. */
const CARD_PUSH_RETRIES = 3;
/** The first retry's delay; each further try waits one more step. */
const CARD_PUSH_RETRY_MS = 2_000;
const CARD_DELETE_CHUNK_SIZE = 500;

function cardDeletesForNonResidentDeckRemovals(
  prev: FlashkaState,
  next: FlashkaState,
  serverTs: Record<string, string>,
  ts: Record<string, string>,
): SyncClientMessage[] {
  const cardsEntity = recordEntities.find((e) => e.name === 'cards');
  const prefix = cardsEntity?.tsKeyPrefix;
  const bulkDeleteType = cardsEntity?.bulkDeleteType;
  if (!prefix || !bulkDeleteType) return [];

  // No meta changed this tick → no deck was deleted this tick. The common case
  // (a card write) then skips the walk over every deck's meta.
  if (prev.deckProperties === next.deckProperties) return [];
  const deckPrefixes: string[] = [];
  for (const [deckId, meta] of Object.entries(next.deckProperties)) {
    if (!meta?.deletedTimeISO) continue;
    if (prev.deckProperties[deckId]?.deletedTimeISO) continue;
    // A bucket that was there is already covered by the bucket diff above.
    if (bucketRowCount(prev.cards[deckId]) > 0) continue;
    deckPrefixes.push(`${prefix}${deckId}${CARD_KEY_DELIM}`);
  }
  if (deckPrefixes.length === 0) return [];

  const deletedTimeISO = new Date().toISOString();
  const keys: Array<{ key: string; deletedTimeISO: string }> = [];
  for (const tsKey of Object.keys(serverTs)) {
    if (!deckPrefixes.some((p) => tsKey.startsWith(p))) continue;
    keys.push({ key: tsKey.slice(prefix.length), deletedTimeISO });
    ts[tsKey] = deletedTimeISO;
  }
  if (keys.length === 0) return [];

  const out: SyncClientMessage[] = [];
  for (let i = 0; i < keys.length; i += CARD_DELETE_CHUNK_SIZE) {
    out.push({
      type: bulkDeleteType,
      keys: keys.slice(i, i + CARD_DELETE_CHUNK_SIZE),
      updatedAtISO: deletedTimeISO,
    } as unknown as SyncClientMessage);
  }
  return out;
}

/**
 * Build a `sync-diff` carrying ONLY the per-record `server*Keys` arrays,
 * derived from `serverTsRef`. No record bodies, no generic sections — so
 * `applySyncDiff` applies nothing, and each entity's `flushLocal` gates on the
 * real server key set (records) / last-synced checkpoint (collections, values).
 * Field names match the server's builder (`entity.serverKeysField` or the
 * `server${Cap(name)}Keys` default).
 */
function buildServerKeysOnlyDiff(
  serverTs: Record<string, string>,
): { type: 'sync-diff' } & Record<string, unknown> {
  const diff: { type: 'sync-diff' } & Record<string, unknown> = { type: 'sync-diff' };
  for (const entity of recordEntities) {
    const prefix = entity.tsKeyPrefix;
    if (!prefix) continue;
    const keys: string[] = [];
    for (const k of Object.keys(serverTs)) {
      if (k.startsWith(prefix)) keys.push(k.slice(prefix.length));
    }
    const field =
      entity.serverKeysField ??
      `server${entity.name.charAt(0).toUpperCase()}${entity.name.slice(1)}Keys`;
    diff[field] = keys;
  }
  return diff;
}

/**
 * Persist collected rows in awaited chunks. Prefers the storage adapter's
 * batched `bulkPut` (web Dexie: one IndexedDB transaction per chunk, awaited);
 * otherwise falls back to per-row `put` + `flush` (native's ring buffer
 * already coalesces a tick's puts into one transaction, and `flush` drains
 * it). Either way each chunk is awaited before the next, so in-flight write
 * buffers stay bounded — this is the fix for the large-account sync-diff
 * renderer OOM. `flush` is optional-chained so test mocks without it no-op.
 */
const PERSIST_CHUNK_SIZE = 2000;

async function flushPendingWrites(
  storage: SyncStorage,
  pendingWrites: Map<string, unknown[]>,
): Promise<void> {
  for (const [table, rows] of pendingWrites) {
    for (let i = 0; i < rows.length; i += PERSIST_CHUNK_SIZE) {
      const chunk = rows.slice(i, i + PERSIST_CHUNK_SIZE);
      if (storage.bulkPut) {
        await storage.bulkPut(table, chunk);
      } else {
        for (const row of chunk) storage.put(table, row);
        await storage.flush?.();
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Aggregate linked-cards-updated handler
// ---------------------------------------------------------------------------

async function applyAggregateLinkedCards(
  msg: Extract<SyncServerMessage, { type: 'linked-cards-updated' }>,
  ctx: SyncCtx,
): Promise<void> {
  // Pending card frames land first: this merge reads the bucket and
  // replaces it, so a later publish of older frames must not win.
  drainPendingBulk('cards');
  await withServerSource(() => {
    const store = useFlashkaStore.getState();
    const existingMap = store.cards[msg.deckId];
    const existingArray = existingMap ? Object.values(existingMap) : [];
    const merged = mergeCardsLWW(existingArray, msg.cards);
    store.setDeckFromSync(msg.deckId, merged, msg.updatedAtISO);
  });
  if (msg.updatedAtISO && msg.deckId) {
    ctx.serverTsRef.current[msg.deckId] = msg.updatedAtISO;
    await ctx.saveServerSyncedTs(ctx.serverTsRef.current);
  }
}
